import { ManualSendoutRequest } from './views/sendout-model';
import { inject, Injectable } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DateTime } from 'luxon';
import { StatisticsFilterInputs } from '@app/models/statistics-filter-inputs';
import { isOnlyInactiveSelected } from '@admin/screen/utils/formValidation.utils';
import { StatisticsApi, StatisticsPerDay } from '@api/index';
import { ChartWeekday, ExposuresAverageResponse } from './statistics.model';
import { MediaStatsEnum } from '@app/shared/models/media-stats';
import { BookingApi } from '@api/services/bookings-api';
import { map, Observable, of, switchMap } from 'rxjs';
import { SystemMessageService } from '@app/core/services/system-message.service';
import { MatDialog } from '@angular/material/dialog';
import { EmailSendoutModalComponent, SendoutModalData } from '@app/shared/components/modals';
import { TranslateConfigService } from '@app/core/translate-config.service';
import { BookingLifecycleStatusEnum } from '../engagement/bookings/model/bookings.model';

@Injectable({
  providedIn: 'root',
})
export class StatisticsService {
  form: FormGroup;

  bookingApi = inject(BookingApi)
  systemMessageService = inject(SystemMessageService)
  dialog = inject(MatDialog)
  statisticsApi = inject(StatisticsApi)
  translateConfigService = inject(TranslateConfigService)

  groupDataBySegment(perDay: StatisticsPerDay[]): {
    segmentId: number;
    perDay: {
      date: string;
      count: number;
    }[];
  }[] {
    return this.groupDataBy('segmentId', perDay);
  }

  groupDataByScreen(perDay: StatisticsPerDay[]): {
    screenId: number;
    perDay: {
      date: string;
      count: number;
    }[];
  }[] {
    return this.groupDataBy('screenId', perDay);
  }

  groupDataByTargetGroup(perDay: StatisticsPerDay[]): {
    targetGroupId: number;
    perDay: {
      date: string;
      count: number;
    }[];
  }[] {
    return this.groupDataBy('targetGroupId', perDay);
  }

  groupDataBy(property: string, perDay: StatisticsPerDay[]) {
    const groupedByProperty: any[] = [];
    perDay.forEach(pd => {
      pd.adSlots.forEach(slot => {
        const group = groupedByProperty.find(g => g[property] === slot[property]);
        if (!group) {
          groupedByProperty.push({
            [property]: slot[property],
            perDay: [
              {
                date: pd.date,
                count: 1,
              },
            ],
          });
        } else {
          const perDay = group.perDay.find(day => day.date === pd.date);
          if (!perDay) {
            group.perDay.push({
              date: pd.date,
              count: 1,
            });
          } else {
            perDay.count++;
          }
        }
      });
    });
    return groupedByProperty;
  }

  buildPeriod(start: DateTime, end: DateTime, columnType: 'day' | 'date' = 'day'): ChartWeekday[] {
    let current = start;
    const days: ChartWeekday[] = [];
    while (current < end) {
      days.push({
        dateTime: current,
        utcString: current.toISO(),
        label: columnType === 'day' ? current.weekdayShort : current.day.toString(),
      });
      current = current.plus({ day: 1 });
    }
    return days;
  }

  initForm(
    initData: Map<string, StatisticsFilterInputs>,
    filterVisibleInputs: (extractionWords: string[], search: string, input: StatisticsFilterInputs, form: any) => void
  ): void {
    if (Object.values(initData)) {
      initData.forEach(content => {
        let formControl: any = new FormControl(content.isChecked);
        if (content.formGroup) {
          formControl = new FormGroup({});
          content.formGroup.forEach(f => {
            formControl.addControl(f.formControlName, new FormControl());
            if (f.isRequired) {
              formControl.get(f.formControlName).setValidators(Validators.required);
            }
          });
        }
        this.form.addControl(content.formControlName, formControl);
        if (content.isRequired) {
          this.form.get(content.formControlName).setValidators(Validators.required);
        }
        if (content.type === 'combobox') {
          this.form.get(content.formControlName).addValidators(isOnlyInactiveSelected);
        }
        if (content.data.length > 0 && content.isFilled)
          this.form.get(content.formControlName).setValue(
            content.isMultipleDropdown
              ? content.data.map(d => {
                return d.id;
              })
              : content.data[content.preselectedOption ?? 0].id
          );
        if (content.search) {
          this.form.addControl('searchTerm' + content.formControlName, new FormControl(''));
          this.form
            .get('searchTerm' + content.formControlName)
            .valueChanges.subscribe(() =>
              filterVisibleInputs(
                content.extractionWords,
                'searchTerm' + content.formControlName,
                initData.get(content.formControlName),
                this.form
              )
            );
        }
        if (content.value) {
          this.form.get(content.formControlName).setValue(
            Array.isArray(content.value)
              ? content.value.map(d => {
                return d.id;
              })
              : content.value
          );
        }
      });
    }
  }

  getGradient(ctx, chartArea, scales) {
    const chartWidth = chartArea.right - chartArea.left;
    const chartHeight = chartArea.bottom - chartArea.top;
    let width: any;
    let gradient: any;
    let height: any;

    if (gradient === null || width !== chartWidth || height !== chartHeight) {
      const pointZero = scales.y.getPixelForValue(0);
      const pointZeroHeight = pointZero - chartArea.top;
      let pointZeroPercentage = isNaN(pointZeroHeight / chartHeight) ? 0 : pointZeroHeight / chartHeight;

      width = chartWidth;
      height = chartHeight;
      gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartHeight + chartArea.top);

      // Clamp pointZeroPercentage between 0 and 1
      pointZeroPercentage = Math.min(Math.max(pointZeroPercentage, 0), 1);

      // Add color stops to handle negative and positive ranges
      if (pointZeroPercentage === 0) {
        // All positive values
        gradient.addColorStop(1, '#1F5792');
        gradient.addColorStop(0, 'white');
      } else if (pointZeroPercentage === 1) {
        // All negative values
        gradient.addColorStop(1, 'white');
        gradient.addColorStop(0, '#FA6200');
      } else {
        // Mixture of positive and negative values
        gradient.addColorStop(0, '#FA6200');
        gradient.addColorStop(pointZeroPercentage, 'white');
        gradient.addColorStop(pointZeroPercentage + (1 - pointZeroPercentage) / 2, '#1F5792');
        gradient.addColorStop(1, '#1F5792');
      }

      return gradient;
    }

    return gradient;
  }

  getRelevantDate(startDate: string, endDate: string) {
    const today = DateTime.local().startOf('day');
    const start = DateTime.fromISO(startDate).startOf('day');
    const end = DateTime.fromISO(endDate).startOf('day');

    if (today >= start && today <= end) {
      return today;
    } else {
      return end;
    }
  }

  generateColorShading(index: number, total: number, color: number): string {
    const hue = color;
    const saturation = 66;
    const minLightness = 34;
    const maxLightness = 75;

    if (total <= 1) {
      return `hsl(${hue}, ${saturation}%, ${minLightness}%)`;
    }

    // Calculate lightness so index 0 is darkest, last is lightest
    const step = (maxLightness - minLightness) / (total - 1);
    const lightness = minLightness + index * step;

    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  }

  generateRandomColor(): string {
    const hue = Math.floor(Math.random() * 360);
    const saturation = 66;
    const minLightness = 34;
    const maxLightness = 75;
    const lightness = Math.floor(Math.random() * (maxLightness - minLightness + 1)) + minLightness;

    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  }

  setTooltip(index: number, chart) {
    const chartRef = chart?.chart;
    if (!chartRef) return;

    const chartArea = chartRef?.chartArea || { left: 0, right: 0, top: 0, bottom: 0 };
    const tooltip = chartRef.tooltip;

    // if index is null it means that the media file is selected with the same index and we want to reset the tooltip
    const isSameIndexActive = index === null;

    const resetTooltip = () => {
      tooltip.setActiveElements([], { x: 0, y: 0 });
    };

    const setTooltip = () => {
      tooltip.setActiveElements(
        [
          {
            datasetIndex: 0,
            index,
          },
        ],
        {
          x: (chartArea.left + chartArea.right) / 2,
          y: (chartArea.top + chartArea.bottom) / 2,
        }
      );
    };

    if (isSameIndexActive) {
      resetTooltip();
    } else {
      setTooltip();
    }

    chart.update();
  }

  formatHours(data, type: MediaStatsEnum): string[] {
    const hours = data.flatMap(item => item[type].map(entry => Number(entry?.hour))).filter(h => !isNaN(h));

    if (hours.length === 0) {
      return [];
    }

    const uniqueSortedHours = Array.from(new Set(hours))
      .map(Number)
      .sort((a, b) => a - b);

    const minHour = uniqueSortedHours[0];
    const maxHour = uniqueSortedHours[uniqueSortedHours.length - 1];
    const filledHours = [];

    for (let h = minHour; h <= maxHour; h++) {
      filledHours.push(h);
    }
    return filledHours.map(h => h.toString().padStart(2, '0') + ':00');
  }

  formatLabels(data: ExposuresAverageResponse) {
    return data.mediaFiles.map((item) => {
      return {
        name: item.name,
        id: item.id
      };
    });
  }

  openCampaignSendoutModal(bookingId: number, bookingStatus?: BookingLifecycleStatusEnum): Observable<ManualSendoutRequest | undefined> {
    const data: SendoutModalData = {
      statusAlert: this.getBookingStatusAlert(bookingStatus),
      showSeparatePdfs: true,
      closeOnSend: true,
    };
    const dialogRef = this.dialog.open(EmailSendoutModalComponent, {
      width: '400px',
      maxHeight: '700px',
      disableClose: true,
      data,
    });

    return dialogRef.afterClosed().pipe(
      map(result => result ? ({ bookingId, ...result } as ManualSendoutRequest) : undefined)
    );
  }

  private getBookingStatusAlert(bookingStatus?: BookingLifecycleStatusEnum): { label: string; type: 'info' | 'success' } | undefined {
    if (bookingStatus === BookingLifecycleStatusEnum.Ongoing) {
      return { label: this.translateConfigService.instant('campaign.sendouts.manual.ongoing'), type: 'info' };
    }
    if (bookingStatus === BookingLifecycleStatusEnum.Completed) {
      return { label: this.translateConfigService.instant('campaign.sendouts.manual.completed'), type: 'success' };
    }
    return undefined;
  }
}
